import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import * as React from 'react';

import {
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import {ButtonStyle} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import {CreateValidationRulesParams} from 'validation_rules/network/types';

import {ConditionEvaluatorVersion} from '../constants';

interface ValidationTextAreaProps {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  required?: boolean;
  maxCharacters?: number | null;
}

function ValidationTextArea({
  label,
  value,
  onChange,
  required = false,
  maxCharacters = null,
}: ValidationTextAreaProps): JSX.Element {
  return (
    <FormGroup>
      <Label>{label}</Label>
      <TextArea
        name={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        rows={3}
        required={required}
      />
      {maxCharacters ? (
        <p className={css(styles.validationCharacterCountText)}>
          {value.length}/{maxCharacters} characters
        </p>
      ) : (
        ''
      )}
    </FormGroup>
  );
}

interface ValidationRulesModalProps {
  hasValidationRulesItemId: string;
  onRequestClose: () => void;
  onRequestSubmit: (createParams: CreateValidationRulesParams) => void;
}

export const PLACEHOLDER_CONDITION: BenchlingExpressionFunction = {
  type: BenchlingExpressionType.FUNCTION,
  parameters: [
    {
      expression: {
        value: 0,
        type: BenchlingExpressionType.CONSTANT_VALUE,
        itemType: {
          name: LeafType.INTEGER,
        },
      },
      name: 'value1',
    },
    {
      expression: {
        value: 0,
        type: BenchlingExpressionType.CONSTANT_VALUE,
        itemType: {
          name: LeafType.INTEGER,
        },
      },
      name: 'value2',
    },
  ],
  name: 'GT',
};

// TODO(BNCH-113130): Update with stub function
function getCondition(): BenchlingExpressionFunction {
  return PLACEHOLDER_CONDITION;
}

export default function ValidationRulesModal(props: ValidationRulesModalProps): JSX.Element {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [definition, setDefinition] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [override, setOverride] = React.useState(false);

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit({
          id: props.hasValidationRulesItemId,
          validationRules: [
            {
              condition: getCondition(),
              errorMessage,
              overridable: override,
              evaluatorVersion: ConditionEvaluatorVersion.V1,
              negateResult: false,
              name,
              description,
            },
          ],
        });
      }}
      title="Create a rule"
      medium={true}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Validate Syntax',
          style: ButtonStyle.TERTIARY_BLUE,
          onClick: noop,
        },
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
        },
      ]}
    >
      <FormGroup>
        <TextField
          label="Name"
          value={name}
          dataTestAttrs={{
            component: 'ValidationRulesModal',
            element: 'inputText',
            key: 'name',
          }}
          onChange={(e: InputChangeEvent) => {
            setName(e.target.value);
          }}
          isRequired={true}
        />
      </FormGroup>
      <ValidationTextArea label="Description" value={description} onChange={setDescription} />
      <FormGroup>
        <Label>Definition</Label>
        <p>If this formula expression is true, display the text defined in the Error Message area.</p>
        <TextArea
          name="Definition"
          value={definition}
          onChange={(e) => {
            setDefinition(e.target.value);
          }}
          rows={10}
        />
      </FormGroup>
      <ValidationTextArea
        label="Error Message"
        value={errorMessage}
        onChange={setErrorMessage}
        required={true}
        maxCharacters={250}
      />
      <CheckboxInput
        id="allow-override-checkbox"
        label="Allow override"
        checked={override}
        onChange={(e) => {
          setOverride(e.target.checked);
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  validationCharacterCountText: {
    textAlign: 'right',
  },
});
